import { localizedStrings } from '@core/strings'
import React from 'react'
import { Box, Flex, Text } from '@fundamentals'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import SelectRegister from '@modules/projects/authGroups/authGroupDetails/permissions/SelectRegister'
import { useGetRegisterItems } from '@core/react-query/features/registers/registers/register/items/hooks'
import { useToast } from '@core/toast'
import { RegisterItemCard } from '@elements'
import { RegisterItem } from '@core/entities/register/RegisterItem'
import { RegisterDetailsPropertyFilters } from '@modules/registers/details/RegisterDetailsPropertyFilters'
import { AllFilterTypes } from '@core/entities/filters'
import { useGetRegisters } from '@core/react-query/features/registers/registers/hooks'
import { useDebouncedValue } from '@core/utils/useDebouncedValue'
import { Virtuoso } from 'react-virtuoso'
import xorBy from 'lodash/xorBy'

type SelectRegisterItemsModalProps = {
  triggerComponent: (onClick) => React.ReactNode
  value?: Array<RegisterItem>
  onChange?: (value: Array<RegisterItem>) => void
  organisationId: number
  projectId: number
  readOnly?: boolean
  registerTemplateId: number
  previewPropertyIds: Array<string>
  pinnedFilterIds?: Array<string>
  allowMultipleSelect?: boolean
}

export const SelectRegisterItemsModal: React.FC<
  SelectRegisterItemsModalProps
> = ({
  value = [],
  onChange,
  organisationId,
  projectId,
  readOnly,
  triggerComponent,
  registerTemplateId,
  previewPropertyIds,
  pinnedFilterIds = [],
  allowMultipleSelect = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [selectedRegisterId, setSelectedRegisterId] = React.useState<
    number | null
  >(null)
  const [term, setTerm] = React.useState<string>('')
  const [filters, setFilters] = React.useState<AllFilterTypes[]>([])
  const toast = useToast()
  const searchTerm = useDebouncedValue(term, 300)

  const { data: registerListResponse } = useGetRegisters({
    organisationId,
    projectIds: projectId ? [projectId] : null,
    includeOrganisationRegisters: true,
    registerTemplateId,
    page: 1,
    size: 10000,
    status: 'LIVE',
  })

  const registerList = React.useMemo(
    () =>
      registerListResponse?.pages[0].content?.map((page) => page).flat() || [],
    [registerListResponse?.pages[0].content],
  )

  const hasOnlyOneRegister = registerList.length === 1

  const registerId = hasOnlyOneRegister
    ? registerList[0].id
    : selectedRegisterId

  const {
    data: registerItemsResponse,
    isFetching: isFetchingRegisterItems,
    hasNextPage,
    fetchNextPage,
  } = useGetRegisterItems(
    {
      registerId,
      term: searchTerm,
      filter: filters,
      sort: 'uniqueId,ASC',
    },
    { enabled: !!registerId, onError: (e) => toast.error(e?.message) },
  )

  const selectRegisterItem = (registerItem: RegisterItem) => {
    if (allowMultipleSelect) {
      onChange(xorBy(value, [registerItem], 'id'))
    } else {
      onChange([registerItem])
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setTerm('')
  }

  const columns = React.useMemo(
    () => registerItemsResponse?.pages[0]?.columns || [],
    [registerItemsResponse?.pages[0]?.columns],
  )

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} fullWidth>
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20, mb: 0 }}
        >
          {localizedStrings.selectRegisterItem}
          <IconButton aria-label='close-modal' onClick={handleClose}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            position='sticky'
            top={0}
            pt={1}
            zIndex={10}
            sx={{
              background: 'white',
            }}
          >
            <Box my={2}>
              <SelectRegister
                required
                organisationId={organisationId}
                projectId={projectId}
                includeOrganisationRegisters={true}
                value={registerId}
                readOnly={hasOnlyOneRegister}
                registerTemplateId={registerTemplateId}
                onChange={(selectedRegisterId) => {
                  setSelectedRegisterId(selectedRegisterId as number)
                  onChange([])
                }}
              />
            </Box>
            {registerId && (
              <>
                <Box my={2}>
                  <TextField
                    value={term}
                    label={localizedStrings.search}
                    onChange={(e) => setTerm(e.target.value)}
                    fullWidth
                    size='small'
                  />
                </Box>
                {!!columns.length && (
                  <Box>
                    <RegisterDetailsPropertyFilters
                      key={registerId}
                      registerId={registerId}
                      registerItemsColumns={columns}
                      useQueryParamStore={false}
                      scope={{
                        type: 'ORGANISATION',
                        organisationId,
                      }}
                      setFilters={setFilters}
                      pinnedFilterIds={pinnedFilterIds}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
          {registerId && (
            <Box id='scrollableContainer' overflow='auto' height='800px'>
              {isFetchingRegisterItems && <LinearProgress />}
              <Virtuoso
                data={
                  registerItemsResponse?.pages
                    .map((page) => page.content)
                    .flat() || []
                }
                context={{ hasNextPage }}
                endReached={() => {
                  if (hasNextPage) {
                    fetchNextPage()
                  }
                }}
                itemContent={(_, registerItem) => {
                  const isSelected = value.some(
                    ({ id }) => id === registerItem.id,
                  )
                  return (
                    <RegisterItemCard
                      registerItem={registerItem}
                      previewPropertyIds={previewPropertyIds}
                      onClick={() => selectRegisterItem(registerItem)}
                      highlight={term}
                      sx={{
                        borderColor: isSelected ? 'primary.main' : null,
                        '&:hover': {
                          borderColor: 'primary.light',
                          cursor: 'pointer',
                        },
                        transition: 'borderColor 0.3s ease',
                      }}
                      icon={
                        <Checkbox
                          checked={isSelected}
                          onChange={() => selectRegisterItem(registerItem)}
                          disabled={readOnly}
                        />
                      }
                    />
                  )
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Flex
            width='100%'
            alignItems='center'
            justifyContent='space-between'
            p={2}
          >
            <Text variant='body2'>
              {localizedStrings.itemsSelectedCount(value.length || 0)}
            </Text>

            <Button variant='contained' onClick={handleClose}>
              {localizedStrings.done}
            </Button>
          </Flex>
        </DialogActions>
      </Dialog>
      {triggerComponent(() => setIsOpen(true))}
    </>
  )
}
